import { List, Record } from 'immutable';

import BaseStore from './BaseStore';
import { ActionTypes } from '../constants/AppConstants';
import Services from '../records/Services';

function _toJson (search) {
    return JSON.stringify(search.serialize());
}

const DisruptionAlertState = new Record({
    alerts: new List(),
    alertsLoading: false,
    searchJsonString: null,
    services: new Services(),
    servicesLoading: false,
    bankHolidays: new List(),
    timetableAlertsWidgetState: {
        alertsChecked: false,
        timetableChangesChecked: false,
        delaysChecked: false,
        stationAlertsChecked: false,
        emailChecked: false,
        appChecked: false,
        tripId: null,
        outwardTrainIds: [],
        inwardTrainIds: [],
    },
    timetableAlerts: new List(),
    timetableAlertsLoading: false,
});

export class DisruptionAlertStore extends BaseStore {

    getInitialState () {
        return new DisruptionAlertState();
    }

    getActionHandlers () {
        return {
            [ActionTypes.DISRUPTION_ALERTS_LOAD_STARTED]: this.handleAlertsLoadStarted,
            [ActionTypes.DISRUPTION_ALERTS_LOAD_FINISHED]: this.handleAlertsLoaded,
            [ActionTypes.DISRUPTION_ALERTS_LOAD_FAILED]: this.handleAlertsLoadFailed,

            [ActionTypes.SERVICE_CHANGE_ALERTS_LOAD_STARTED]: this.handleServiceChangeAlertsLoadStarted,
            [ActionTypes.SERVICE_CHANGE_ALERTS_LOAD_FINISHED]: this.handleServiceChangeAlertsLoaded,
            [ActionTypes.SERVICE_CHANGE_ALERTS_LOAD_FAILED]: this.handleServiceChangeAlertsLoadFailed,

            [ActionTypes.DISRUPTION_ALERT_CREATED]: this.handleAlertCreated,
            [ActionTypes.DISRUPTION_ALERT_UPDATED]: this.handleAlertUpdated,
            [ActionTypes.DISRUPTION_ALERT_DELETED]: this.handleAlertDeleted,
            [ActionTypes.DISRUPTION_ALERT_MUTE_CREATED]: this.handleAlertMuteCreated,
            [ActionTypes.DISRUPTION_ALERT_MUTE_UPDATED]: this.handleAlertMuteUpdated,
            [ActionTypes.DISRUPTION_ALERT_MUTE_DELETED]: this.handleAlertMuteDeleted,
            [ActionTypes.DISRUPTION_ALERTS_SEARCH_STARTED]: this.handleServiceSearchStarted,
            [ActionTypes.DISRUPTION_ALERTS_SEARCH_FINISHED]: this.handleServiceSearchFinished,
            [ActionTypes.DISRUPTION_ALERTS_SEARCH_FAILED]: this.handleServiceSearchFailed,
            [ActionTypes.DISRUPTION_ALERTS_SEARCH_EARLIER_STARTED]: this.handleServiceSearchStarted,
            [ActionTypes.DISRUPTION_ALERTS_SEARCH_EARLIER_FINISHED]: this.handleServiceSearchFinished,
            [ActionTypes.DISRUPTION_ALERTS_SEARCH_LATER_STARTED]: this.handleServiceSearchStarted,
            [ActionTypes.DISRUPTION_ALERTS_SEARCH_LATER_FINISHED]: this.handleServiceSearchFinished,
            [ActionTypes.DISRUPTION_ALERTS_SERVICES_RESET]: this.handleServicesReset,
            [ActionTypes.DISRUPTION_ALERTS_FETCH_BANK_HOLIDAYS_FINISHED]: this.handleFetchBankHolidays,
            [ActionTypes.SET_TIMETABLE_ALERTS_WIDGET_STATE]: this.setTimetableAlertsWidgetState,
            [ActionTypes.GET_TIMETABLE_ALERTS_WIDGET_STATE]: this.getTimetableAlertsWidgetState,
            [ActionTypes.ADD_TIMETABLE_ALERT]: this.addTimetableAlert,
            [ActionTypes.RESET_TIMETABLE_ALERTS]: this.resetTimetableAlerts,
            [ActionTypes.SERVICE_CHANGE_ALERT_DELETED]: this.handleTimetableAlertDeleted,
        };
    }

    handleAlertsLoadStarted () {
        this.setState(this.state.merge({ alertsLoading: true }));
    }

    handleAlertsLoaded ({ alerts }) {
        this.setState(this.state.merge({
            alerts,
            alertsLoading: false,
        }));
    }

    handleAlertsLoadFailed () {
        this.setState(this.state.merge({ alertsLoading: false }));
    }

    handleServiceChangeAlertsLoadStarted () {
        this.setState(this.state.merge({ timetableAlertsLoading: true }));
    }

    handleServiceChangeAlertsLoaded ({ timetableAlerts }) {
        this.setState(this.state.merge({
            timetableAlerts,
            timetableAlertsLoading: false,
        }));
    }

    handleServiceChangeAlertsLoadFailed () {
        this.setState(this.state.merge({ timetableAlertsLoading: false }));
    }

    setTimetableAlertsWidgetState ({ formState }) {
        this.setState(this.state.merge({ timetableAlertsWidgetState: formState }));
    }

    addTimetableAlert () {
        const item = this.state.timetableAlertsWidgetState;
        this.setState(this.state.merge({ timetableAlerts: this.state.timetableAlerts.push(item) }));
    }

    getTimetableAlertsWidgetState () {
        return this.get('timetableAlertsWidgetState');
    }

    getTimetableAlerts () {
        return this.get('timetableAlerts');
    }

    resetTimetableAlerts() {
        this.setState(this.state.merge({ timetableAlerts: new List() }));
    }

    getAlerts () {
        return this.get('alerts');
    }

    hasMatchingAlert ({ order, orderTrip }) {
        return this.getAlerts().filter(alert => alert.matchesOrderTrip(order, orderTrip)).size;
    }

    handleAlertCreated ({ alert }) {
        const alerts = this.getAlerts().push(alert);
        this.handleAlertsLoaded({ alerts });
    }

    handleAlertUpdated ({ alert }) {
        let alerts = this.getAlerts();
        const index = alerts.findIndex(x => x.id === alert.id);
        alerts = alerts.setIn([index], alert);
        this.handleAlertsLoaded({ alerts });
    }

    handleAlertDeleted ({ alert }) {
        const alerts = this.getAlerts().filterNot(x => x === alert);
        this.handleAlertsLoaded({ alerts });
    }

    handleTimetableAlertDeleted ({ alrt }) {
        const alerts = this.getTimetableAlerts().filterNot(x => x === alrt);
        this.handleServiceChangeAlertsLoaded({ timetableAlerts: alerts });
    }

    handleAlertMuteCreated ({ alert, alertMute }) {
        let alerts = this.getAlerts();
        const index = alerts.findIndex(x => x.id === alert.id);
        const updatedAlert = alert.set('mutes', alert.mutes.push(alertMute));
        alerts = alerts.setIn([index], updatedAlert);
        this.handleAlertsLoaded({ alerts });
    }

    /*
        TO DO:

        This method has proven difficult to unit test and may not
        actually work as expected in all cases.

        It probably needs refactoring, unit testing and regression testing.
    */
    handleAlertMuteUpdated ({ alert, alertMute }) {
        let alerts = this.getAlerts();
        const alertIndex = alerts.findIndex(x => x.id === alert.id);
        const alertMuteIndex = alerts.getIn([alertIndex, 'mutes']).findIndex(x => x.id === alertMute.id);
        const updatedAlert = alert.setIn([alertIndex, 'mutes', alertMuteIndex], alertMute);
        alerts = alerts.setIn([alertIndex], updatedAlert);
        this.handleAlertsLoaded({ alerts });
    }

    handleAlertMuteDeleted ({ alert, alertMute }) {
        let alerts = this.getAlerts();
        const alertIndex = alerts.findIndex(x => x.id === alert.id);
        const updatedAlert = alert.set('mutes', alert.mutes.filterNot(x => x === alertMute));
        alerts = alerts.setIn([alertIndex], updatedAlert);
        this.handleAlertsLoaded({ alerts });
    }

    handleServiceSearchStarted ({ search }) {
        this.setState(this.state.merge({
            searchJsonString: _toJson(search),
            servicesLoading: true,
        }));
    }

    handleServiceSearchFinished ({ search, servicesList }) {
        this.setState(this.state.merge({
            searchJsonString: _toJson(search),
            services: new Services({ outward: servicesList }).orderForSearch(search),
            servicesLoading: false,
        }));
    }

    handleServiceSearchFailed () {
        this.setState(this.state.merge({ servicesLoading: false }));
    }

    handleServicesReset () {
        this.setState(this.state.merge({ services: new Services() }));
    }

    handleFetchBankHolidays ({ dates }) {
        this.setState(this.state.merge({ bankHolidays: dates }));
    }

    getBankHolidays () {
        return this.get('bankHolidays');
    }

}

export default new DisruptionAlertStore();
